import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardOnboardingResponse } from '../../../../queries/pxtraCard';
import { Button } from '../../atomic/button';
import { FeedbackAlert } from '../../atomic/feedback/alert';
import { RoundedPaper } from '../../atomic/surface/roundedPaper';
import { MonthlyVolumeDialog } from './monthlyVolumeDialog';
import { TaxIdBlock } from './taxIdBlock';

type Props = {
  companyId: string;
  onboarding: CardOnboardingResponse;
};

const Section: FC<{ title: string; children: ReactNode; description?: string }> = ({
  title,
  description,
  children
}) => (
  <RoundedPaper className="mb-4 border border-solid border-grey-50 p-6">
    <p className="m-0 text-base font-bold text-primary-main">{title}</p>
    {description && <p className="m-0 mt-1 text-sm text-grey-600">{description}</p>}
    <div className="mt-4">{children}</div>
  </RoundedPaper>
);

const Row: FC<{ label: string; value?: string; action?: ReactNode }> = ({
  label,
  value,
  action
}) =>
  value || action ? (
    <div className="flex flex-wrap items-center justify-between gap-3 border-0 border-b border-solid border-grey-50 py-3 last:border-b-0 last:pb-0 first:pt-0">
      <span className="text-sm text-grey-600">{label}</span>
      <div className="flex items-center gap-3">
        {value && <span className="text-sm font-medium text-primary-main">{value}</span>}
        {action}
      </div>
    </div>
  ) : null;

export const SettingsFace: FC<Props> = ({ companyId, onboarding }) => {
  const { t } = useTranslation();
  const [volumeOpen, setVolumeOpen] = useState(false);

  const { company } = onboarding;
  const taxStep = onboarding.steps.find((step) => step.id === 'tax_id');
  const identification = onboarding.steps.find((step) => step.id === 'identification');
  // Swan lets only the person who signed approve cards, and pxtra can act
  // for them only as one of its own managers.
  const approverMissing = identification?.payload.approver_linked === false;
  const volume = company.monthly_payment_volume;

  return (
    <>
      {approverMissing && (
        <div className="mb-4">
          <FeedbackAlert
            severity="warning"
            bulletPoints={false}
            title={t('pxtraCard:settingsApproverMissingTitle')}
            description={t('pxtraCard:settingsApproverMissing', {
              name: company.legal_representative_name ?? ''
            })}
          />
        </div>
      )}

      {onboarding.verified_at && (
        <div className="mb-4 flex items-center gap-2">
          <span className="h-2 w-2 shrink-0 rounded-full bg-success-main" />
          <span className="text-sm text-grey-700">
            {t('pxtraCard:settingsVerifiedAt', {
              date: new Date(onboarding.verified_at).toLocaleDateString('de-DE')
            })}
          </span>
        </div>
      )}

      <Section
        title={t('pxtraCard:settingsCompanySection')}
        description={t('pxtraCard:settingsContactHint')}
      >
        <Row label={t('pxtraCard:settingsCompanyName')} value={company.name} />
        <Row
          label={t('pxtraCard:settingsLegalRepresentative')}
          value={company.legal_representative_name}
        />
        <Row label={t('pxtraCard:settingsIban')} value={company.iban} />
      </Section>

      <Section
        title={t('pxtraCard:settingsWalletSection')}
        description={t('pxtraCard:settingsMonthlyVolumeHint')}
      >
        <Row
          label={t('pxtraCard:settingsMonthlyVolume')}
          value={volume ? t(`pxtraCard:volume_${volume}`) : t('pxtraCard:settingsNotSet')}
          action={
            <Button variant="secondary" size="small" onClick={() => setVolumeOpen(true)}>
              {t('pxtraCard:settingsChange')}
            </Button>
          }
        />
      </Section>

      {taxStep?.payload.subjects?.length ? (
        <Section
          title={t('pxtraCard:step_tax_id')}
          description={t('pxtraCard:step_taxIdDescription')}
        >
          <TaxIdBlock companyId={companyId} subjects={taxStep.payload.subjects} />
        </Section>
      ) : null}

      <MonthlyVolumeDialog
        companyId={companyId}
        current={volume ?? ''}
        open={volumeOpen}
        onClose={() => setVolumeOpen(false)}
      />
    </>
  );
};
